import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const SITE_URL = 'https://idemenu.com';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

/**
 * SEO Component for managing meta tags, Open Graph, Twitter Cards, and JSON-LD
 * 
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Meta description
 * @param {string} props.path - URL path (e.g., '/about')
 * @param {string} props.image - OG image URL
 * @param {string} props.type - OG type (website, article, product, etc.)
 * @param {Object} props.schema - JSON-LD structured data
 * @param {boolean} props.noindex - Whether to noindex the page
 */
export default function SEO({
  title,
  description,
  path = '',
  image = DEFAULT_IMAGE,
  type = 'website',
  schema = null,
  noindex = false,
  children
}) {
  const { i18n } = useTranslation();
  const currentLang = i18n.language || 'tr';
  const alternateLang = currentLang === 'tr' ? 'en' : 'tr';
  
  const canonicalUrl = `${SITE_URL}${path}`;
  const fullTitle = title ? `${title} | idemenu` : 'idemenu - Dijital Menü Yönetim Sistemi';
  
  // Default Organization schema
  const defaultSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'idemenu',
    url: SITE_URL,
    logo: `${SITE_URL}/idemenu-logo.svg`,
    description: 'QR kodlu dijital menü ve restoran yönetim sistemi',
    sameAs: [
      'https://twitter.com/idemenu',
      'https://instagram.com/idemenu',
      'https://linkedin.com/company/idemenu'
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'info@idemenu.com',
      contactType: 'customer service',
      availableLanguage: ['Turkish', 'English']
    }
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={currentLang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Hreflang for multi-language */}
      <link rel="alternate" hrefLang={currentLang} href={canonicalUrl} />
      <link rel="alternate" hrefLang={alternateLang} href={`${canonicalUrl}?lang=${alternateLang}`} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={currentLang === 'tr' ? 'tr_TR' : 'en_US'} />
      <meta property="og:locale:alternate" content={currentLang === 'tr' ? 'en_US' : 'tr_TR'} />
      <meta property="og:site_name" content="idemenu" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@idemenu" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Additional Meta */}
      <meta name="author" content="idemenu" />
      <meta name="publisher" content="idemenu" />
      <meta name="theme-color" content="#7c3aed" />
      
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(schema || defaultSchema)}
      </script>
      
      {children}
    </Helmet>
  );
}

// Pre-configured SEO for common page types
export function LandingSEO() {
  const { t, i18n } = useTranslation();
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'idemenu',
    url: SITE_URL,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web Browser',
    description: i18n.language === 'tr' 
      ? 'QR kodlu dijital menü ve restoran yönetim sistemi. Kafeler, restoranlar ve oteller için.'
      : 'Digital menu with QR code and restaurant management system. For cafes, restaurants, and hotels.',
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'TRY',
      lowPrice: '0',
      highPrice: '499',
      offerCount: '3'
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      ratingCount: '127',
      bestRating: '5',
      worstRating: '1'
    },
    creator: {
      '@type': 'Organization',
      name: 'idemenu',
      url: SITE_URL
    }
  };
  
  return (
    <SEO
      title={null}
      description={t('landing.hero.description', 'QR kodlu dijital menü ile restoranınızı modernleştirin. Kolay kurulum, çoklu dil desteği, gerçek zamanlı güncellemeler.')}
      path=""
      schema={schema}
    />
  );
}

export function AboutSEO() {
  const { i18n } = useTranslation();
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: i18n.language === 'tr' ? 'Hakkımızda - idemenu' : 'About Us - idemenu',
    url: `${SITE_URL}/about`,
    mainEntity: {
      '@type': 'Organization',
      name: 'idemenu',
      foundingDate: '2024',
      foundingLocation: 'Turkey',
      description: i18n.language === 'tr'
        ? 'idemenu, restoran ve kafeler için dijital menü çözümleri sunan bir Türk teknoloji şirketidir.'
        : 'idemenu is a Turkish technology company providing digital menu solutions for restaurants and cafes.'
    }
  };
  
  return (
    <SEO
      title={i18n.language === 'tr' ? 'Hakkımızda' : 'About Us'}
      description={i18n.language === 'tr' 
        ? 'idemenu hakkında bilgi edinin. Türkiye\'de geliştirilen dijital menü ve restoran yönetim sistemi.'
        : 'Learn about idemenu. Digital menu and restaurant management system developed in Turkey.'}
      path="/about"
      schema={schema}
    />
  );
}

export function ContactSEO() {
  const { i18n } = useTranslation();
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: i18n.language === 'tr' ? 'İletişim - idemenu' : 'Contact - idemenu',
    url: `${SITE_URL}/contact`,
    mainEntity: {
      '@type': 'Organization',
      name: 'idemenu',
      email: 'info@idemenu.com',
      contactPoint: {
        '@type': 'ContactPoint',
        email: 'info@idemenu.com',
        contactType: 'customer support',
        availableLanguage: ['Turkish', 'English']
      }
    }
  };
  
  return (
    <SEO
      title={i18n.language === 'tr' ? 'İletişim' : 'Contact'}
      description={i18n.language === 'tr'
        ? 'idemenu ile iletişime geçin. Sorularınız, önerileriniz veya destek talepleriniz için bize ulaşın.'
        : 'Get in touch with idemenu. Contact us for questions, suggestions, or support requests.'}
      path="/contact"
      schema={schema}
    />
  );
}

export function FeaturesSEO() {
  const { i18n } = useTranslation();
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: i18n.language === 'tr' ? 'idemenu Özellikleri' : 'idemenu Features',
    url: `${SITE_URL}/features`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: i18n.language === 'tr' ? 'QR Kod Menü' : 'QR Code Menu',
        description: i18n.language === 'tr' 
          ? 'Müşterileriniz QR kodu tarayarak menünüze anında erişsin'
          : 'Customers can instantly access your menu by scanning QR code'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: i18n.language === 'tr' ? 'Çoklu Dil' : 'Multi-Language',
        description: i18n.language === 'tr'
          ? 'Menünüzü Türkçe ve İngilizce olarak sunun'
          : 'Present your menu in Turkish and English'
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: i18n.language === 'tr' ? 'Gerçek Zamanlı Güncelleme' : 'Real-time Updates',
        description: i18n.language === 'tr'
          ? 'Fiyat ve ürün değişikliklerini anında yansıtın'
          : 'Reflect price and product changes instantly'
      }
    ]
  };
  
  return (
    <SEO
      title={i18n.language === 'tr' ? 'Özellikler' : 'Features'}
      description={i18n.language === 'tr'
        ? 'idemenu özellikleri: QR kod menü, çoklu dil desteği, gerçek zamanlı güncelleme, özelleştirilebilir tasarım ve daha fazlası.'
        : 'idemenu features: QR code menu, multi-language support, real-time updates, customizable design and more.'}
      path="/features"
      schema={schema}
    />
  );
}

export function PricingSEO() {
  const { i18n } = useTranslation();
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'idemenu',
    description: i18n.language === 'tr'
      ? 'Dijital menü ve restoran yönetim sistemi'
      : 'Digital menu and restaurant management system',
    brand: {
      '@type': 'Brand',
      name: 'idemenu'
    },
    offers: [
      {
        '@type': 'Offer',
        name: 'Starter',
        price: '0',
        priceCurrency: 'TRY',
        description: i18n.language === 'tr' ? 'Küçük işletmeler için ücretsiz plan' : 'Free plan for small businesses'
      },
      {
        '@type': 'Offer',
        name: 'Pro',
        price: '199',
        priceCurrency: 'TRY',
        priceValidUntil: '2027-12-31',
        description: i18n.language === 'tr' ? 'Büyüyen işletmeler için profesyonel plan' : 'Professional plan for growing businesses'
      },
      {
        '@type': 'Offer',
        name: 'Enterprise',
        price: '499',
        priceCurrency: 'TRY',
        priceValidUntil: '2027-12-31',
        description: i18n.language === 'tr' ? 'Kurumsal işletmeler için özel çözümler' : 'Custom solutions for enterprise businesses'
      }
    ]
  };
  
  return (
    <SEO
      title={i18n.language === 'tr' ? 'Fiyatlandırma' : 'Pricing'}
      description={i18n.language === 'tr'
        ? 'idemenu fiyatları: Ücretsiz başlangıç planı, Pro ve Enterprise seçenekleri. İhtiyacınıza uygun planı seçin.'
        : 'idemenu pricing: Free starter plan, Pro and Enterprise options. Choose the plan that fits your needs.'}
      path="/pricing"
      schema={schema}
    />
  );
}

export function TermsSEO() {
  const { i18n } = useTranslation();
  
  return (
    <SEO
      title={i18n.language === 'tr' ? 'Kullanım Koşulları' : 'Terms of Service'}
      description={i18n.language === 'tr'
        ? 'idemenu kullanım koşulları ve şartları. Hizmetimizi kullanmadan önce lütfen okuyun.'
        : 'idemenu terms of service and conditions. Please read before using our service.'}
      path="/terms"
      schema={{
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: i18n.language === 'tr' ? 'Kullanım Koşulları' : 'Terms of Service',
        url: `${SITE_URL}/terms`
      }}
    />
  );
}

export function PrivacySEO() {
  const { i18n } = useTranslation();
  
  return (
    <SEO
      title={i18n.language === 'tr' ? 'Gizlilik Politikası' : 'Privacy Policy'}
      description={i18n.language === 'tr'
        ? 'idemenu gizlilik politikası. Verilerinizi nasıl topladığımız, kullandığımız ve koruduğumuz hakkında bilgi.'
        : 'idemenu privacy policy. Information about how we collect, use, and protect your data.'}
      path="/privacy"
      schema={{
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: i18n.language === 'tr' ? 'Gizlilik Politikası' : 'Privacy Policy',
        url: `${SITE_URL}/privacy`
      }}
    />
  );
}

export function KvkkSEO() {
  const { i18n } = useTranslation();
  
  return (
    <SEO
      title="KVKK Aydınlatma Metni"
      description="idemenu KVKK (Kişisel Verilerin Korunması Kanunu) aydınlatma metni. Kişisel verilerinizin işlenmesi hakkında bilgi."
      path="/kvkk"
      schema={{
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'KVKK Aydınlatma Metni',
        url: `${SITE_URL}/kvkk`,
        inLanguage: 'tr'
      }}
    />
  );
}

export function CookiesSEO() {
  const { i18n } = useTranslation();
  
  return (
    <SEO
      title={i18n.language === 'tr' ? 'Çerez Politikası' : 'Cookie Policy'}
      description={i18n.language === 'tr'
        ? 'idemenu çerez politikası. Web sitemizde kullanılan çerezler hakkında bilgi.'
        : 'idemenu cookie policy. Information about cookies used on our website.'}
      path="/cookies"
      schema={{
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: i18n.language === 'tr' ? 'Çerez Politikası' : 'Cookie Policy',
        url: `${SITE_URL}/cookies`
      }}
    />
  );
}

/**
 * SEO for PublicMenu (restaurant menu pages)
 * @param {Object} props
 * @param {string} props.restaurantName - Restaurant/business name
 * @param {string} props.slug - Tenant slug
 * @param {string} props.description - Restaurant description
 * @param {string} props.logo - Restaurant logo URL
 * @param {Array} props.categories - Menu categories
 */
export function PublicMenuSEO({ restaurantName, slug, description, logo, categories = [] }) {
  const { i18n } = useTranslation();
  
  const menuUrl = `${SITE_URL}/menu/${slug}`;
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Restaurant',
    name: restaurantName,
    url: menuUrl,
    image: logo,
    description: description || `${restaurantName} dijital menüsü`,
    servesCuisine: categories.map(c => c.name).join(', '),
    hasMenu: {
      '@type': 'Menu',
      name: `${restaurantName} Menü`,
      url: menuUrl,
      hasMenuSection: categories.map((category, idx) => ({
        '@type': 'MenuSection',
        name: category.name,
        position: idx + 1
      }))
    },
    potentialAction: {
      '@type': 'ViewAction',
      target: menuUrl
    }
  };
  
  return (
    <SEO
      title={`${restaurantName} - ${i18n.language === 'tr' ? 'Dijital Menü' : 'Digital Menu'}`}
      description={description || `${restaurantName} ${i18n.language === 'tr' ? 'dijital menüsünü görüntüleyin' : 'view digital menu'}`}
      path={`/menu/${slug}`}
      image={logo || DEFAULT_IMAGE}
      type="restaurant.menu"
      schema={schema}
    />
  );
}
